import { memo, useRef } from "react";

import EntropyMenuList from "./components/EntropyMenuList";
import FunctionMenuList from "./components/FunctionMenuList";
import RangeList from "./components/RangeList";
import RegionMenuList from "./components/RegionMenuList";
import SvgRectangleList from "./components/SvgRectangleList";
import VisualizationTooltip from "./components/VisualizationTooltip";

import { Structure } from "../../@types/visualization";
import { SVG_PARAMS } from "./consts";
import useHover from "./hooks/useHover";
import { useTooltip } from "./hooks/useTooltip";
import {
  calculateSvgHeight,
  checkForErrorMessage,
  generateRegionLinks
} from "./utils";

type Props = {
  previewType: string;
  sha1: string;
  sourceID?: number;
  relationshipId?: number;
  structure?: Structure;
  hasPlot: boolean;
};

const Visualization = ({
  previewType,
  sha1,
  sourceID,
  relationshipId,
  structure,
  hasPlot
}: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { open, close, data, changePage, tooltipRef } = useTooltip();
  const { regionClass, addClass, removeClass } = useHover();

  const regionLinks = generateRegionLinks(sha1, sourceID, relationshipId);

  const { plot } = structure ?? {};
  const { matrix, total, columns } = plot ?? {};
  const { ranges, styles, functions } = matrix ?? {};
  const { heatmap, region } = styles ?? {};
  const { blocks = "0", extras } = total ?? {};

  const title = hasPlot ? plot?.title : "Can't generate structure plot!";
  const hasRanges = Boolean(hasPlot && ranges);

  const errorMessage = checkForErrorMessage(hasPlot, hasRanges, sha1);
  const showVisualization = hasPlot && hasRanges;

  const svgHeight = calculateSvgHeight(blocks, extras);
  const sidebarType = previewType === "structure" ? "region" : "entropy";

  return (
    <div
      ref={containerRef}
      id="file-visualization-module"
      className={`file-visualization file-visualization--${previewType} ${regionClass}`}
    >
      <h2 className="file-visualization__title">{title}</h2>
      {errorMessage && <p>{errorMessage}</p>}

      {showVisualization && (
        <>
          <RangeList
            height={svgHeight}
            ranges={ranges ?? []}
            columns={columns}
            regionLinks={regionLinks}
          />

          <SvgRectangleList
            height={svgHeight}
            matrix={matrix}
            previewType={previewType}
            functions={functions ?? []}
            onMouseEnter={(instructions, names, event) =>
              open(instructions, names, event, containerRef?.current)
            }
          />

          <ul
            className={`file-visualization__sidebar-list file-visualization__sidebar-list--${sidebarType}`}
          >
            {sidebarType === "region" ? (
              <>
                <RegionMenuList
                  regions={region ?? []}
                  matrix={matrix}
                  regionLinks={regionLinks}
                  onMouseEnter={addClass}
                  onMouseLeave={removeClass}
                />
                <FunctionMenuList
                  functions={functions ?? []}
                  onMouseEnter={addClass}
                  onMouseLeave={removeClass}
                />
              </>
            ) : (
              <EntropyMenuList
                heatmap={heatmap ?? []}
                onMouseEnter={addClass}
                onMouseLeave={removeClass}
              />
            )}
          </ul>

          {data.tooltipKey && (
            <VisualizationTooltip
              title={title}
              tooltipData={data}
              closeTooltip={close}
              changeTooltipPage={changePage}
              width={SVG_PARAMS.tooltipW}
              ref={tooltipRef}
            />
          )}
        </>
      )}
    </div>
  );
};

export default memo(Visualization);
